import { Router } from '@angular/router';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DataService } from 'src/app/features/dfte-grid-view/config/data.service';
import { loginService } from './login.service';
import { MessageService } from 'src/app/features/dfte-grid-view/services/message.service';
import {
  BasicAuthUserTokenPayload,
  IBasicAuthUserBackendTokenPayload,
  IBasicAuthUserTokenPayload,
} from 'src/app/shared/constants/dfte-user-info';
import { MessagingService } from 'src/app/shared/services/messaging.service';
import { CookieService } from 'ngx-cookie-service';
import { BUS_MESSAGE_KEY } from 'src/app/shared/constants/message-bus';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, of } from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit {
  firstLogin: boolean = false;
  basicAuthUserData: IBasicAuthUserTokenPayload;
  loginForm: FormGroup;
  passwordForm: FormGroup;
  passwordValidations = {
    hasLowercase: false,
    hasUppercase: false,
    hasDigit: false,
    hasMinLength: false,
    hasOneSpecialCharacter: false,
  };
  userData: BehaviorSubject<boolean>;
  loggedUserBasicAuthData: IBasicAuthUserTokenPayload;
  containerData$: Observable<any>;
  firstLoginToken: any;

  constructor(
    public _data: DataService,
    private fb: FormBuilder,
    private loginService: loginService,
    private router: Router,
    private messageService: MessageService,
    private messagingService: MessagingService,
    private cookieService: CookieService
  ) {}

  ngOnInit(): void {
    try {
      this.initializePasswordForm();

      let authToken = localStorage.getItem('authToken');

      if (authToken) {
        const decodedToken: IBasicAuthUserBackendTokenPayload =
          jwtDecode(authToken);

        if (decodedToken.isFirstLogin) {
          this.firstLogin = false;

          localStorage.removeItem('authToken'); // Clear the token
          this.messageService.showMessage(
            'Your session is expired, please relogin'
          );
          this.loginService.logout().subscribe(() => {
            this.messageService.showMessage(
              'Your session is expired, please re-login',
              'error'
            );
            this.messagingService.publish(BUS_MESSAGE_KEY.USER_DETAILS, null); // Reset user details
            this.router.navigate(['/login']); // Redirect to login
          });
        } else {
          this.router.navigate(['/']);
        }
      } else {
        this.router.navigate(['/login']);
      }
    } catch (message) {
      this.messageService.showMessage(
        'Please enter Login Credentials',
        'error'
      );
    }

    this.unmaskPage();
    this.initializeForm();

    this.passwordForm.get('newPassword')?.valueChanges.subscribe((value) => {
      this.updatePasswordValidations(value);
      this.passwordForm.get('newPassword')?.markAsTouched(); // Manually mark as touched
    });

    this.passwordForm
      .get('confirmPassword')
      ?.valueChanges.subscribe((value) => {
        this.updatePasswordValidations(value);
      });
  }

  unmaskPage() {
    this._data.apiCallInProgress = false;
    const overlay = document.getElementById('overlay');
    if (overlay) overlay.style.display = 'none';
  }

  maskPage() {
    this._data.apiCallInProgress = true;
    const overlay = document.getElementById('overlay');
    if (overlay) overlay.style.display = 'block';
  }

  initializeForm() {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });
  }

  login() {
    if (this.loginForm.invalid) {
      console.error('Form is invalid');
      return;
    }
    this.maskPage();

    const { username, password } = this.loginForm.value;

    this.loginService.login({ username, password }).subscribe(
      (response: any) => {
        try {
          const decodedToken: IBasicAuthUserBackendTokenPayload = jwtDecode(
            response.token
          );
          this.firstLoginToken = response.token;
          //  localStorage.setItem('authToken', JSON.stringify(response.token));
          localStorage.setItem('isAdmin', JSON.stringify(decodedToken.isAdmin));

          let refinedToken: IBasicAuthUserTokenPayload =
            this.mapTokenToInterface(decodedToken);
          this.basicAuthUserData = new BasicAuthUserTokenPayload(refinedToken);

          this.passwordForm.get('username')?.setValue(decodedToken.username);

          this.messagingService.publish(
            BUS_MESSAGE_KEY.USER_DETAILS,
            this.basicAuthUserData
          );

          this.messagingService.subscribe(
            BUS_MESSAGE_KEY.USER_DETAILS,
            (data) => {
              this.userData = data;
              this.containerData$ = of({
                email: data.email,
                name: data.userName,
              });
              this.loggedUserBasicAuthData = data;
              this._data.loggedUserId = this.loggedUserBasicAuthData.email;
            }
          );

          this.cookieService.set(
            BUS_MESSAGE_KEY.USER_DETAILS,
            JSON.stringify(this.basicAuthUserData)
          );

          if (decodedToken.isFirstLogin) {
            this.firstLogin = true;
          } else {
            localStorage.setItem('authToken', JSON.stringify(response.token));
            this.router.navigate(['/']);
          }
        } catch (message) {
          this.handleError(message);
        }
      },
      (error: any) => {
        console.log(error);
        this.handleError(error); // Handle error dynamically
      },
      () => {
        this.unmaskPage();
      }
    );
  }

  handleError(error: any) {
    this.unmaskPage();

    const errorMessage =
      error.error?.message ||
      'An unexpected error occurred. Please try again later.';

    this.messageService.showMessage(errorMessage, 'error');
  }

  mapTokenToInterface(token) {
    const mappedToken = { ...token };

    mappedToken.isAdmin = Boolean(mappedToken.isAdmin);
    mappedToken.isUserActive = Boolean(mappedToken.isUserActive);
    mappedToken.isFirstLogin = Boolean(mappedToken.isFirstLogin);

    mappedToken.email = mappedToken.username;
    mappedToken.userName = mappedToken.name;

    return mappedToken;
  }

  initializePasswordForm() {
    this.passwordForm = this.fb.group(
      {
        username: [{ value: '', disabled: true }],
        currentPassword: ['', Validators.required],
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(15),
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/
            ),
          ],
        ],
        confirmPassword: ['', Validators.required],
      },
      { validator: this.passwordMatchValidator }
    );
  }

  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

      return newPassword === confirmPassword ? null : { mismatch: true };
    

  }

  updatePasswordValidations(password: string) {
    this.passwordValidations.hasLowercase = /[a-z]/.test(password);
    this.passwordValidations.hasUppercase = /[A-Z]/.test(password);
    this.passwordValidations.hasDigit = /\d/.test(password);
    this.passwordValidations.hasMinLength =
      password.length >= 8 && password.length <= 15;
    this.passwordValidations.hasOneSpecialCharacter =
      /[!#@$%&'*+/=?^_`{|}~-]/.test(password);

    this.passwordForm.updateValueAndValidity();
  }

  onSubmitPasswordChange() {
    if (this.passwordForm.invalid) {
      return;
    }
    this.firstLogin = false;
    this.maskPage();

    const { currentPassword, newPassword } = this.passwordForm.value;
    const username = this.passwordForm.get('username')?.value;
    const token = JSON.stringify(this.firstLoginToken);
    const requestBody = {
      username,
      currentPassword,
      newPassword,
    };

    this.loginService.updatePassword(requestBody, token).subscribe(
      (response) => {
        this.messageService.showMessage(
          'Password successfully changed',
          'success'
        );
        this.firstLogin = false;
        this.unmaskPage();
      },
      (error) => {
        this.handleError(error);
        this.unmaskPage();
      }
    );
    this.loginForm.patchValue({
      password: '',
    });
  }
}
